/**
 * ReleasePostDrawer Component
 *
 * AI-powered release post generator from Loom videos.
 * Creates both blog posts and email campaigns.
 *
 * Story: RELEASE-002 - Multi-Video Support & Manual Transcript Input
 */
import { useState, useEffect, useCallback } from 'react'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Plus } from 'lucide-react'
import { releasePostSchema, type ReleasePostFormData, type VideoEntry } from '@/schemas/releasePost.schema'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { useDrawer } from '@/components/ui/drawer'
import { VideoEntryCard } from '@/components/blog/VideoEntryCard'
import { AIGenerationManager } from '@/components/blog/AIGenerationManager'

interface ReleasePostDrawerProps {
  drawerId: string
  onComplete?: (postId: string) => void
  onStateChange?: (state: {
    isExtracting: boolean
    isSubmitting: boolean
    isValid: boolean
    handleExtract: () => void
    handleSubmit: () => void
  }) => void
}

export function ReleasePostDrawer({
  drawerId,
  onComplete,
  onStateChange,
}: ReleasePostDrawerProps) {
  const { closeDrawer, updateDrawer } = useDrawer()
  const [isExtracting, setIsExtracting] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<ReleasePostFormData>({
    resolver: zodResolver(releasePostSchema),
    mode: 'onChange',
    defaultValues: {
      videos: [
        {
          loom_url: '',
          transcript: '',
          title: '',
          description: '',
          input_method: 'auto_extract',
        },
      ],
      release_type: 'minor',
      target_audience: 'all_users',
    },
  })

  const videos = watch('videos') || []

  const addVideo = () => {
    if (videos.length < 5) {
      setValue('videos', [
        ...videos,
        {
          loom_url: '',
          transcript: '',
          title: '',
          description: '',
          input_method: 'auto_extract',
        },
      ])
    }
  }

  const removeVideo = (index: number) => {
    setValue(
      'videos',
      videos.filter((_, i) => i !== index)
    )
  }

  const updateVideo = (index: number, updatedVideo: VideoEntry) => {
    const newVideos = [...videos]
    newVideos[index] = updatedVideo
    setValue('videos', newVideos)
  }

  const handleExtractTranscript = async (index: number) => {
    const video = videos[index]
    if (!video.loom_url) {
      toast.error('Please enter a Loom URL first')
      return
    }

    setIsExtracting(index)
    try {
      const result = await apiService.releasePosts.extractLoom({ loom_url: video.loom_url })

      // Update video with extracted transcript
      updateVideo(index, {
        ...video,
        transcript: result.transcript_full,
        title: video.title || result.title,
      })

      toast.success(`Transcript extracted: ${result.transcript_segments_count} segments`)
    } catch (error: unknown) {
      // Show appropriate error message
      const err = error as {
        response?: {
          status?: number
          data?: {
            detail?: string | Array<{ msg: string; type: string }>
          }
        }
      }

      if (err.response?.status === 501) {
        toast.info('Transcript preview not yet available. You can still generate the full release post.')
      } else if (err.response?.status === 422 && Array.isArray(err.response.data?.detail)) {
        const validationErrors = err.response.data.detail.map((e: { msg: string }) => e.msg).join(', ')
        toast.error(`Validation error: ${validationErrors}`)
      } else if (typeof err.response?.data?.detail === 'string') {
        toast.error(err.response.data.detail)
      } else {
        toast.error('Failed to extract transcript')
      }
      console.error(error)
    } finally {
      setIsExtracting(null)
    }
  }

  const onSubmitForm: SubmitHandler<ReleasePostFormData> = useCallback(async (data: ReleasePostFormData) => {
    setIsSubmitting(true)
    try {
      // Generate temporary title and slug from date
      const dateSlug = data.release_date.replace(/\//g, '-')
      const timestamp = Date.now()

      // First, create a draft blog post to get post_id
      const createPostResponse = await apiService.blog.posts.create({
        title: `Release Update ${data.release_date} (Generating...)`,
        slug: `release-${dateSlug}-${timestamp}`,
        content: JSON.stringify({ type: 'doc', content: [] }), // Empty TipTap doc
        status: 'draft',
      })

      const postId = createPostResponse.id

      // Then, start release post generation with multiple videos
      // Backend will auto-generate version number from date
      await apiService.releasePosts.generate({
        post_id: postId,
        videos: data.videos,
        release_version: data.release_version?.trim() || undefined, // Pass undefined if empty
        release_date: data.release_date,
        release_type: data.release_type,
        target_audience: data.target_audience,
        feature_summary: data.feature_summary,
      })

      toast.success(`Release post generation started with ${data.videos.length} video(s)!`)

      // Replace drawer content with loading state manager
      updateDrawer(drawerId, {
        content: (
          <AIGenerationManager
            postId={postId}
            drawerId={drawerId}
            onComplete={(completedPostId) => {
              if (onComplete) {
                onComplete(completedPostId)
              }
              // Optionally navigate to edit the post
              // navigate(`/blog/posts/${completedPostId}/edit`)
            }}
          />
        ),
        footer: null // AIGenerationManager handles its own footer
      })
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number
          data?: {
            detail?: string | Array<{ msg: string; type: string }>
          }
        }
      }

      // Handle FastAPI validation errors (422)
      if (err.response?.status === 422 && Array.isArray(err.response.data?.detail)) {
        const validationErrors = err.response.data.detail.map((e: { msg: string }) => e.msg).join(', ')
        toast.error(`Validation error: ${validationErrors}`)
      } else if (typeof err.response?.data?.detail === 'string') {
        toast.error(err.response.data.detail)
      } else {
        toast.error('Failed to start release post generation')
      }
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }, [onComplete, closeDrawer, drawerId, updateDrawer])

  // Notify parent of state changes for footer updates
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        isExtracting: isExtracting !== null,
        isSubmitting,
        isValid,
        handleExtract: () => {}, // Not used in multi-video mode
        handleSubmit: handleSubmit(onSubmitForm),
      })
    }
  }, [isExtracting, isSubmitting, isValid, onStateChange, handleSubmit, onSubmitForm])

  return (
    <div className="space-y-6 p-4">
      <div>
        <p className="text-sm text-muted-foreground">
          Add one or more demo videos to automatically generate a release announcement
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
        {/* Videos Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-foreground font-semibold">Demo Videos</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addVideo}
              disabled={videos.length >= 5}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Video
            </Button>
          </div>

          {videos.map((video, index) => (
            <VideoEntryCard
              key={index}
              video={video}
              index={index}
              canRemove={videos.length > 1}
              onUpdate={(updated) => updateVideo(index, updated)}
              onRemove={() => removeVideo(index)}
              onExtract={() => handleExtractTranscript(index)}
              isExtracting={isExtracting === index}
            />
          ))}

          {errors.videos && (
            <p className="text-sm text-destructive">{errors.videos.message}</p>
          )}
        </div>

        {/* Release Date */}
        <div className="space-y-2">
          <Label htmlFor="release_date" className="text-foreground">
            Release Date *
          </Label>
          <Input
            id="release_date"
            type="date"
            className="bg-background border-border text-foreground"
            {...register('release_date')}
          />
          {errors.release_date && (
            <p className="text-sm text-destructive mt-1">
              {errors.release_date.message}
            </p>
          )}
        </div>

        {/* Release Type */}
        <div className="space-y-2">
          <Label htmlFor="release_type" className="text-foreground">
            Release Type *
          </Label>
          <Select
            value={watch('release_type')}
            onValueChange={(value) => setValue('release_type', value as 'major' | 'minor' | 'patch' | 'feature')}
          >
            <SelectTrigger className="bg-background border-border text-foreground">
              <SelectValue placeholder="Select release type" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="major">Major Release (Breaking changes)</SelectItem>
              <SelectItem value="minor">Minor Release (New features)</SelectItem>
              <SelectItem value="patch">Patch Release (Bug fixes)</SelectItem>
              <SelectItem value="feature">Feature Release (Specific feature)</SelectItem>
            </SelectContent>
          </Select>
          {errors.release_type && (
            <p className="text-sm text-destructive mt-1">
              {errors.release_type.message}
            </p>
          )}
        </div>

        {/* Target Audience */}
        <div className="space-y-2">
          <Label htmlFor="target_audience" className="text-foreground">
            Target Audience *
          </Label>
          <Select
            value={watch('target_audience')}
            onValueChange={(value) => setValue('target_audience', value as 'all_users' | 'admins' | 'developers')}
          >
            <SelectTrigger className="bg-background border-border text-foreground">
              <SelectValue placeholder="Select target audience" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all_users">All Users</SelectItem>
              <SelectItem value="admins">Administrators Only</SelectItem>
              <SelectItem value="developers">Developers Only</SelectItem>
            </SelectContent>
          </Select>
          {errors.target_audience && (
            <p className="text-sm text-destructive mt-1">
              {errors.target_audience.message}
            </p>
          )}
        </div>

        {/* Feature Summary (Optional) */}
        <div className="space-y-2">
          <Label htmlFor="feature_summary" className="text-foreground">
            Feature Summary (Optional)
          </Label>
          <Textarea
            id="feature_summary"
            placeholder="Brief summary to help AI understand the context..."
            className="bg-background border-border text-foreground min-h-[100px]"
            {...register('feature_summary')}
          />
          <p className="text-xs text-muted-foreground">
            Add any additional context about the features that might not be clear from the videos
          </p>
        </div>
      </form>
    </div>
  )
}
